import type { AggregateDefinition, AggregateInstance } from "./aggregate";
import type { CodeableConcept, Period } from "./common";
import { InvalidTransitionError, PreconditionFailedError } from "./errors";
import type { DomainId } from "./lib/domain-id";
import type { PatientId } from "./patient.model";
import type { PractitionerId } from "./practitioner.model";
import type { VisitId } from "./visit.model";

// ============================================================
// Flag — encapsulated Flag aggregate (ADR-0017 pilot)
// ============================================================

export type FlagStatus = "active" | "inactive" | "entered-in-error";

const FLAG_STATUSES: ReadonlySet<string> = new Set(["active", "inactive", "entered-in-error"]);
/** Stable domain identity. Deliberately unrelated to the FHIR Flag resource id. */
export type FlagId = DomainId<"FlagId">;

export function asFlagId(value: string): FlagId {
  if (!value) throw new PreconditionFailedError("A flag identity cannot be empty");
  return value as FlagId;
}

interface FlagFields {
  /** Required — the alert itself (safety/admin concern). Free-text allowed. */
  code: CodeableConcept;
  subject: { id: PatientId };
  /** flag-category coding(s) — safety, admin, clinical, etc. */
  category?: CodeableConcept[];
  /** When the flag became/ceases to be relevant. `end` is set on resolve. */
  period?: Period;
  author?: { id: PractitionerId };
  encounter?: { id: VisitId };
}

/** What application code supplies when raising a new flag. */
export interface RaiseFlagInput extends FlagFields {
  id: FlagId;
}

/** Complete serializable domain state. Contains no FHIR persistence locator. */
export interface FlagSnapshot extends FlagFields {
  id: FlagId;
  status: FlagStatus;
}

export interface UpdateFlagCommand {
  code?: CodeableConcept;
  category?: CodeableConcept[];
}

function assertHasCode(code: CodeableConcept | undefined): asserts code is CodeableConcept {
  if (!code?.coding?.length && !code?.text) {
    throw new PreconditionFailedError("Flag must have a code");
  }
}

export class Flag implements AggregateInstance<FlagSnapshot> {
  private readonly _id: FlagId;
  private _status: FlagStatus;
  private _code: CodeableConcept;
  readonly subject: { id: PatientId };
  private _category?: CodeableConcept[];
  private _period?: Period;
  readonly author?: { id: PractitionerId };
  readonly encounter?: { id: VisitId };

  private constructor(props: FlagSnapshot) {
    assertHasCode(props.code);
    const p = structuredClone(props);
    this._id = p.id;
    this._status = p.status;
    this._code = p.code;
    this.subject = p.subject;
    this._category = p.category;
    this._period = p.period;
    this.author = p.author;
    this.encounter = p.encounter;
  }

  /** Domain creation establishes identity and initial lifecycle state. */
  static raise(input: RaiseFlagInput): Flag {
    return new Flag({ ...structuredClone(input), id: asFlagId(input.id), status: "active" });
  }

  /** Reconstruction accepts lifecycle states that cannot be chosen at creation. */
  static restore(snapshot: FlagSnapshot): Flag {
    if (!FLAG_STATUSES.has(snapshot.status)) {
      throw new PreconditionFailedError(`Cannot restore a flag with unknown status '${snapshot.status}'`);
    }
    return new Flag(snapshot);
  }

  get id(): FlagId {
    return this._id;
  }

  get status(): FlagStatus {
    return this._status;
  }

  /** Resolve an active flag — status→inactive, stamp period.end. */
  resolve(now: Date): void {
    if (this._status !== "active") {
      throw new InvalidTransitionError(this._status, "Flag:resolve");
    }
    this._status = "inactive";
    this._period = { ...this._period, end: now.toISOString() };
  }

  update(command: UpdateFlagCommand): void {
    if (this._status === "entered-in-error") {
      throw new PreconditionFailedError("Cannot update a flag in terminal status 'entered-in-error'");
    }
    if (command.code !== undefined) {
      assertHasCode(command.code);
      this._code = structuredClone(command.code);
    }
    if (command.category !== undefined) {
      this._category = structuredClone(command.category);
    }
  }

  snapshot(): FlagSnapshot {
    return structuredClone({
      id: this._id,
      status: this._status,
      code: this._code,
      subject: this.subject,
      category: this._category,
      period: this._period,
      author: this.author,
      encounter: this.encounter,
    });
  }
}

/**
 * Composed generic surface for infrastructure. Domain callers retain the
 * meaningful `Flag.raise(...)` vocabulary.
 */
export const flagAggregate = {
  create: (input: RaiseFlagInput) => Flag.raise(input),
  restore: (snapshot: FlagSnapshot) => Flag.restore(snapshot),
} satisfies AggregateDefinition<Flag, RaiseFlagInput, FlagSnapshot>;
